// src/Layouts/GatePasses/GatePassDashboard.tsx
//
// Gate pass admin screen. Early departures and short leaves are kept apart:
// the pass type is the top-level switch, and everything below it — status
// chips, table columns, wording, colour — belongs to that one type only.
//
//   Early departure (orange) → student goes home for the rest of the day
//   Short leave     (teal)   → student steps out and comes back
//
// Data: GET /gate-passes with pass_type always set, plus date / status.
// The counts on the type switch come from the same endpoint with limit=1
// (total_items), since the backend has no separate counts endpoint.
//
// Row actions: view (ViewGatePassModal — approval, OTP and gate actions),
// quick approve (PUT /:id/approve) and cancel (PUT /:id/cancel).
//
// Short leaves are approved on creation (no approval step, no OTP). Their
// tab has a scan box — one POST /gate-passes/scan per scan: a student who is
// out is marked back in school and their row blinks; anyone else opens
// "New short leave" with that student filled in (no extra search call).
// Saving a short leave also records the exit in the same request.
// The Early departures tab has the same scan box: an approved pass is marked
// gone home, a pending one opens for approval, anyone else gets a new form.

import React, { useCallback, useEffect, useRef, useState, JSX } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import CustomPaginator from '../../components/CustomPaginator';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import GatePassFormModal from './parts/GatePassFormModal';
import ViewGatePassModal from './parts/ViewGatePassModal';
import VisitorLogSkeletonLoader from '../../skeletons/VisitorLogSkeletonLoader';
import { GlobalDateField } from '../../components/GlobalComponents/GlobalDateField';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';
import {
  ApiGatePass,
  GatePassPagination,
  GatePassRecord,
  GatePassStatus,
  OVERDUE_META,
  PASS_TYPE_META,
  RELATION_ALONE,
  PassType,
  STATUSES_BY_TYPE,
  STATUS_META,
  formatPassDate,
  formatTime,
  getInitials,
  isOverdue,
  mapApiGatePass,
  mapSearchStudent,
  StudentSearchResult,
  returnTiming,
  statusLabel,
  toDateParam,
} from './parts/types';

type DateScope = 'today' | 'all';
type StatusFilter = 'all' | GatePassStatus;

const PASS_TYPES: PassType[] = ['early_departure', 'short_leave'];

interface TypeCounts {
  early_departure: number;
  short_leave: number;
  outNow: number; // short leaves currently outside the school
}

interface ViewTarget {
  id: string;
  studentName: string;
  spStudentId: string;
}

// ── Scan box (both tabs) ────────────────────────────────────────────────────
// One request per scan: POST /gate-passes/scan { code: SP ID, pass_type: tab }.
//   "returned"  → short leave: the student was out and is now back in school
//   "gone_home" → early departure: approved pass, the student has now left
//   "new"       → nothing to act on — open the new-pass form with the student
//   "open_pass" → they already have an open pass today (e.g. still pending) — open it

const SCAN_HINT: Record<PassType, string> = {
  short_leave: 'Out on leave → back in school · anyone else → new short leave',
  early_departure: 'Approved → gone home · pending → opens to approve · anyone else → new early departure',
};

type ScanResult =
  | { action: 'returned' | 'gone_home'; passId: string; message: string }
  | { action: 'new'; student: StudentSearchResult }
  | { action: 'open_pass'; passId: string; passType: PassType; studentName: string; spStudentId: string; message: string };

const ScanPanel: React.FC<{
  passType: PassType;
  onResult: (r: ScanResult) => void;
  toast: React.RefObject<Toast | null>;
}> = ({ passType, onResult, toast }) => {
  const meta = PASS_TYPE_META[passType];
  const boxRef = useRef<HTMLDivElement>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastDone, setLastDone] = useState<string | null>(null);

  const scan = async () => {
    const value = code.trim();
    if (!value || busy) return;
    setBusy(true);
    try {
      const res = await apiClient.post('api/gate-passes/scan', { code: value, pass_type: passType });
      const d = res.data;
      if (d.status !== 'success') {
        toast.current?.show({ severity: 'error', summary: 'Scan', detail: d.message, life: 4000 });
        return;
      }
      setCode('');
      if (d.action === 'returned' || d.action === 'gone_home') {
        setLastDone(d.message);
        onResult({ action: d.action, passId: String(d.gate_pass_id), message: d.message });
      } else if (d.action === 'open_pass') {
        onResult({
          action: 'open_pass',
          passId: String(d.gate_pass_id),
          passType: d.pass_type,
          studentName: d.student_name,
          spStudentId: d.sp_student_id,
          message: d.message,
        });
      } else {
        onResult({ action: 'new', student: mapSearchStudent(d.student) });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Scan failed.',
        life: 5000,
      });
    } finally {
      setBusy(false);
      boxRef.current?.querySelector('input')?.focus();
    }
  };

  return (
    <div className="gp-return" style={{ borderColor: meta.border, borderLeftColor: meta.color }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div className="gp-return-icon" style={{ background: meta.gradient }}>
          <i className={busy ? 'pi pi-spin pi-spinner' : 'pi pi-qrcode'} />
        </div>
        <div style={{ minWidth: 160 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: meta.color }}>Scan ID card</div>
          <div style={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>{SCAN_HINT[passType]}</div>
        </div>
        <div
          ref={boxRef}
          style={{ flex: '1 1 280px', maxWidth: 420, marginLeft: 'auto' }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              scan();
            } else if (e.key === 'Escape') {
              setCode('');
            }
          }}
        >
          <GlobalInputField
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Scan ID card, or type SP ID + Enter"
            icon="pi pi-qrcode"
            autoComplete="off"
          />
        </div>
      </div>

      {lastDone && (
        <div className="gp-return-done">
          <i className="pi pi-check-circle" />
          {lastDone}
        </div>
      )}
    </div>
  );
};

const GatePassDashboard: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [passType, setPassType] = useState<PassType>('early_departure');
  const [dateScope, setDateScope] = useState<DateScope>('today');
  const [date, setDate] = useState<Date | null>(null);
  const [status, setStatus] = useState<StatusFilter>('all');

  const [passes, setPasses] = useState<GatePassRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState<TypeCounts | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState<GatePassPagination>({
    current_page: 1,
    per_page: 10,
    total_items: 0,
    total_pages: 1,
  });

  const [viewTarget, setViewTarget] = useState<ViewTarget | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [busyRowId, setBusyRowId] = useState<string | null>(null);
  const [prefillStudent, setPrefillStudent] = useState<StudentSearchResult | null>(null); // from a scan
  const [flashId, setFlashId] = useState<string | null>(null); // row that blinks after a scan
  const pendingFlashRef = useRef<string | null>(null);

  const typeMeta = PASS_TYPE_META[passType];
  const isShortLeave = passType === 'short_leave';

  // Date param shared by the list and the counts.
  const dateParam = dateScope === 'today' ? toDateParam(new Date()) : date ? toDateParam(date) : '';

  // silent: keep the table on screen (no skeleton) — used after a scan.
  const fetchPasses = useCallback(async (silent = false) => {
    // A refetch made to show a just-marked row stays quiet too.
    const quiet = silent || !!pendingFlashRef.current;
    if (!quiet) setLoading(true);
    try {
      const params: any = { page, limit: pageSize, pass_type: passType };
      if (status !== 'all') params.status = status;
      if (dateParam) params.date = dateParam;

      const res = await apiClient.get('api/gate-passes', { params });
      const data = res.data;

      if (data.status === 'success') {
        const rows: ApiGatePass[] = data.data || [];
        setPasses(rows.map(mapApiGatePass));
        setPagination(data.pagination);
        // Blink the row we came here to show (it's on top: newest activity first).
        const flash = pendingFlashRef.current;
        if (flash) {
          pendingFlashRef.current = null;
          setFlashId(flash);
          setTimeout(() => setFlashId((cur) => (cur === flash ? null : cur)), 1600);
          // Bring it on screen if the page is scrolled down.
          requestAnimationFrame(() =>
            document.querySelector('tr.gp-row-flash')?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
          );
        }
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Not loaded',
          detail: data.message || 'Gate passes could not be loaded.',
          life: 4000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Could not reach gate passes.',
        life: 5000,
      });
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [page, pageSize, passType, status, dateParam]);

  // Totals for the type switch. A failure here only hides the numbers.
  const fetchCounts = useCallback(async () => {
    const total = async (extra: Record<string, string>) => {
      const params: any = { page: 1, limit: 1, ...extra };
      if (dateParam) params.date = dateParam;
      const res = await apiClient.get('api/gate-passes', { params });
      return res.data.status === 'success' ? Number(res.data.pagination?.total_items ?? 0) : 0;
    };
    try {
      const [early, short, outNow] = await Promise.all([
        total({ pass_type: 'early_departure' }),
        total({ pass_type: 'short_leave' }),
        total({ pass_type: 'short_leave', status: 'out' }),
      ]);
      setCounts({ early_departure: early, short_leave: short, outNow });
    } catch {
      setCounts(null);
    }
  }, [dateParam]);

  useEffect(() => {
    fetchPasses();
  }, [fetchPasses]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const refreshAll = () => {
    fetchPasses();
    fetchCounts();
  };

  // Refresh in place (no skeleton), then blink the row twice.
  // The list is sorted by latest activity, so the row just marked is the
  // first row of page 1 with no status filter. Jump there if we're elsewhere
  // (page 3, "Out on leave" chip, another date) — the refetch that follows
  // blinks it.
  const refreshAndFlash = (passId: string) => {
    fetchCounts();
    pendingFlashRef.current = passId;
    const onOtherDate = dateScope === 'all' && !!date && toDateParam(date) !== toDateParam(new Date());
    if (page !== 1 || status !== 'all' || onOtherDate) {
      setPage(1);
      setStatus('all');
      if (onOtherDate) setDate(null);
      return; // the effect refetches with the new page / filter
    }
    fetchPasses(true);
  };

  const openNewLeave = (student: StudentSearchResult | null = null) => {
    setPrefillStudent(student);
    setIsFormOpen(true);
  };

  const handleScanResult = (r: ScanResult) => {
    switch (r.action) {
      case 'returned':
      case 'gone_home':
        refreshAndFlash(r.passId);
        break;
      case 'new':
        openNewLeave(r.student);
        break;
      case 'open_pass':
        toast.current?.show({ severity: 'info', summary: 'Open pass', detail: r.message, life: 4000 });
        openView({ id: r.passId, studentName: r.studentName, spStudentId: r.spStudentId });
        break;
    }
  };

  // Row gate action — short leave out → back in (IN); approved early
  // departure → gone home (OUT). One call, then blink.
  const handleRowGate = async (row: GatePassRecord, direction: 'IN' | 'OUT') => {
    setBusyRowId(row.id);
    try {
      const res = await apiClient.post('api/movements', {
        student_id: row.studentId,
        direction,
        gate: 'main',
        method: 'manual',
      });
      if (res.data.status === 'success') {
        refreshAndFlash(row.id);
      } else {
        toast.current?.show({ severity: 'error', summary: 'Not marked', detail: res.data.message, life: 5000 });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Could not record the gate entry.',
        life: 5000,
      });
    } finally {
      setBusyRowId(null);
    }
  };

  const resetToFirstPage = () => setPage(1);

  const handleTypeChange = (next: PassType) => {
    if (next === passType) return;
    setPassType(next);
    setStatus('all'); // status chips differ per type
    resetToFirstPage();
  };

  const handleScopeChange = (next: DateScope) => {
    if (next === dateScope) return;
    setDateScope(next);
    setDate(null);
    resetToFirstPage();
  };

  const openView = (target: ViewTarget) => {
    setViewTarget(target);
    setIsViewOpen(true);
  };

  const handleQuickApprove = async (row: GatePassRecord) => {
    setBusyRowId(row.id);
    try {
      const res = await apiClient.put(`api/gate-passes/${row.id}/approve`);
      if (res.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Approved',
          detail: `${row.studentName} can now leave through the gate.`,
          life: 4000,
        });
        refreshAll();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: res.data.message || 'The gate pass could not be approved.',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Something went wrong while approving.',
        life: 5000,
      });
    } finally {
      setBusyRowId(null);
    }
  };

  const handleCancel = async (row: GatePassRecord) => {
    const confirmed = window.confirm(
      `Cancel the ${PASS_TYPE_META[row.passType].label.toLowerCase()} for ${row.studentName}? The record is kept with status "Cancelled".`,
    );
    if (!confirmed) return;

    setBusyRowId(row.id);
    try {
      const res = await apiClient.put(`api/gate-passes/${row.id}/cancel`, {});
      if (res.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Cancelled',
          detail: 'The gate pass was cancelled.',
          life: 4000,
        });
        refreshAll();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: res.data.message || 'The gate pass could not be cancelled.',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Something went wrong while cancelling.',
        life: 5000,
      });
    } finally {
      setBusyRowId(null);
    }
  };

  const handleFormSuccess = (created: ViewTarget & { passType: PassType }) => {
    setIsFormOpen(false);
    // A short leave is already out — show it (blink) on top of its tab.
    if (created.passType === 'short_leave') {
      if (passType === 'short_leave') {
        refreshAndFlash(created.id);
      } else {
        pendingFlashRef.current = created.id;
        setPassType('short_leave');
        setStatus('all');
        setPage(1);
        fetchCounts();
      }
      return;
    }
    // Otherwise land on the tab the new pass belongs to; an early departure
    // opens straight away for approval.
    if (created.passType !== passType) {
      setPassType(created.passType);
      setStatus('all');
    }
    setPage(1);
    refreshAll();
    if (created.passType === 'early_departure') openView(created);
  };

  // ── column templates ─────────────────────────────────────────────────────

  const serialTemplate = (_: GatePassRecord, options: ColumnBodyOptions): JSX.Element => (
    <div style={serialStyle}>{options.rowIndex + (page - 1) * pageSize + 1}</div>
  );

  const studentTemplate = (row: GatePassRecord): JSX.Element => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ ...avatarStyle, background: typeMeta.gradient }}>{getInitials(row.studentName)}</div>
      <div style={{ minWidth: 0 }}>
        <div style={nameStyle}>{row.studentName || '—'}</div>
        <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{row.spStudentId}</div>
      </div>
    </div>
  );

  const reasonTemplate = (row: GatePassRecord): JSX.Element => (
    <div style={{ ...ellipsisStyle, maxWidth: 220, fontSize: 13, color: '#374151' }} title={row.reason}>
      {row.reason}
    </div>
  );

  const pickupTemplate = (row: GatePassRecord): JSX.Element =>
    row.relation === RELATION_ALONE ? (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#475569' }}>
        <i className="pi pi-user" style={{ fontSize: 11, color: '#0f766e' }} />
        Alone
      </div>
    ) : (
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{row.pickedUpBy}</div>
        <div style={{ fontSize: 11, color: '#9ca3af' }}>
          {row.relation}
          {row.contactNo && ` · ${row.contactNo}`}
        </div>
      </div>
    );

  const dateTemplate = (row: GatePassRecord): JSX.Element => (
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{formatPassDate(row.date)}</div>
  );

  const requestedTemplate = (row: GatePassRecord): JSX.Element => (
    <div style={{ fontSize: 12.5, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>
      {formatTime(row.createdAt)}
    </div>
  );

  // Short leave: when the student actually went out.
  const wentOutTemplate = (row: GatePassRecord): JSX.Element =>
    row.outAt ? (
      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>{formatTime(row.outAt)}</div>
    ) : (
      <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>
    );

  // Short leave: when the student actually came back, against the back-by time.
  const backInTemplate = (row: GatePassRecord): JSX.Element => {
    if (!row.inAt) return <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>;
    const timing = returnTiming(row.inAt, row.expectedReturnAt);
    return (
      <div style={{ whiteSpace: 'nowrap' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 700, color: '#15803d' }}>
          <i className="pi pi-sign-in" style={{ fontSize: 11 }} />
          {formatTime(row.inAt)}
        </div>
        {timing && (
          <div style={{ fontSize: 11, fontWeight: 600, color: timing.late ? '#b91c1c' : '#64748b' }}>{timing.label}</div>
        )}
      </div>
    );
  };

  // Short leave: the agreed back-by time (optional).
  const backByTemplate = (row: GatePassRecord): JSX.Element => {
    const overdue = isOverdue(row);
    const returned = row.status === 'returned';
    if (!row.expectedReturnAt) {
      return <div style={{ fontSize: 12.5, fontWeight: 500, color: '#94a3b8' }}>Not set</div>;
    }
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12.5,
          fontWeight: 700,
          whiteSpace: 'nowrap',
          color: overdue ? '#b91c1c' : returned ? '#94a3b8' : typeMeta.color,
        }}
      >
        <i className={overdue ? 'pi pi-exclamation-triangle' : 'pi pi-hourglass'} style={{ fontSize: 11 }} />
        {formatTime(row.expectedReturnAt)}
      </div>
    );
  };

  const approvalTemplate = (row: GatePassRecord): JSX.Element => {
    if (!row.approvalMethod) {
      return <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>;
    }
    const isOtp = row.approvalMethod === 'otp';
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 12,
          fontWeight: 600,
          color: '#475569',
          whiteSpace: 'nowrap',
        }}
      >
        <i className={isOtp ? 'pi pi-mobile' : 'pi pi-building'} style={{ fontSize: 11 }} />
        {isOtp ? 'Parent OTP' : 'School'}
      </span>
    );
  };

  const statusTemplate = (row: GatePassRecord): JSX.Element => {
    const overdue = isOverdue(row);
    const meta = overdue ? OVERDUE_META : STATUS_META[row.status];
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '4px 10px',
          borderRadius: 20,
          fontSize: 11.5,
          fontWeight: 700,
          color: meta.color,
          background: meta.bg,
          border: `1px solid ${meta.border}`,
          whiteSpace: 'nowrap',
        }}
      >
        <i className={meta.icon} style={{ fontSize: 11 }} />
        {overdue ? meta.label : statusLabel(row.status, row.passType)}
      </span>
    );
  };

  const actionTemplate = (row: GatePassRecord): JSX.Element => {
    const busy = busyRowId === row.id;
    const canCancel = row.status === 'pending' || row.status === 'approved';
    return (
      <div style={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button
          icon="pi pi-eye"
          className="p-button-text p-button-rounded"
          tooltip="View"
          tooltipOptions={{ position: 'top' }}
          style={{ color: '#60a5fa', width: 32, height: 32 }}
          disabled={busy}
          onClick={() => openView({ id: row.id, studentName: row.studentName, spStudentId: row.spStudentId })}
        />
        {row.status === 'pending' && (
          <Button
            icon="pi pi-check"
            className="p-button-text p-button-rounded"
            tooltip="Approve (no OTP)"
            tooltipOptions={{ position: 'top' }}
            style={{ color: '#16a34a', width: 32, height: 32 }}
            disabled={busy}
            onClick={() => handleQuickApprove(row)}
          />
        )}
        {row.status === 'out' && row.passType === 'short_leave' && (
          <Button
            icon={busy ? 'pi pi-spin pi-spinner' : 'pi pi-sign-in'}
            className="p-button-text p-button-rounded"
            tooltip="Back in school"
            tooltipOptions={{ position: 'top' }}
            style={{ color: '#0f766e', width: 32, height: 32 }}
            disabled={busy}
            onClick={() => handleRowGate(row, 'IN')}
          />
        )}
        {row.status === 'approved' && row.passType === 'early_departure' && (
          <Button
            icon={busy ? 'pi pi-spin pi-spinner' : 'pi pi-home'}
            className="p-button-text p-button-rounded"
            tooltip="Gone home"
            tooltipOptions={{ position: 'top' }}
            style={{ color: '#c2410c', width: 32, height: 32 }}
            disabled={busy}
            onClick={() => handleRowGate(row, 'OUT')}
          />
        )}
        {canCancel && (
          <Button
            icon="pi pi-ban"
            className="p-button-text p-button-rounded"
            tooltip="Cancel pass"
            tooltipOptions={{ position: 'top' }}
            style={{ color: '#ef4444', width: 32, height: 32 }}
            disabled={busy}
            onClick={() => handleCancel(row)}
          />
        )}
      </div>
    );
  };

  // ── empty state copy ─────────────────────────────────────────────────────

  const emptyState = (() => {
    const noun = typeMeta.plural.toLowerCase();
    if (status !== 'all')
      return {
        icon: 'pi pi-filter',
        title: `No ${noun} in this status`,
        message: 'Switch to All to see the rest.',
      };
    if (dateScope === 'today')
      return {
        icon: typeMeta.icon,
        title: `No ${noun} today`,
        message: isShortLeave
          ? 'Create one when a student needs to step out and come back.'
          : 'Create one when a student needs to go home before school ends.',
      };
    if (date)
      return {
        icon: 'pi pi-calendar',
        title: `No ${noun} on this date`,
        message: 'Pick another date or clear it to see every pass.',
      };
    return {
      icon: 'pi pi-inbox',
      title: `No ${noun} yet`,
      message: 'Passes you create will show up here.',
    };
  })();

  const statusOptions: StatusFilter[] = ['all', ...STATUSES_BY_TYPE[passType]];
  const scopeLabel = dateScope === 'today' ? 'today' : date ? `on ${formatPassDate(toDateParam(date))}` : 'in total';

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
        <style>{`
          @keyframes slideIn { from { opacity:0; transform:translateX(-10px) } to { opacity:1; transform:translateX(0) } }
          .p-datatable .p-datatable-tbody > tr { animation:slideIn .35s ease-out; transition:background .15s; }
          .p-datatable .p-datatable-tbody > tr:hover { background:rgba(65,105,225,.04)!important; box-shadow:inset 4px 0 0 0 var(--gp-accent); }
          .p-datatable .p-datatable-thead > tr > th { background:#f9fafb!important; color:#6b7280!important; font-size:11px!important; font-weight:700!important; text-transform:uppercase!important; letter-spacing:.8px!important; padding:16px 12px!important; border-bottom:1px solid #e5e7eb!important; }
          .p-datatable .p-datatable-tbody > tr > td { padding:14px 12px!important; border-bottom:1px solid #f3f4f6!important; }
          .p-datatable .p-datatable-tbody > tr:last-child > td { border-bottom:none!important; }
          .p-button-text:hover { background:#f3f4f6!important; }

          .gp-types { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:14px; margin-bottom:16px; }
          @media (max-width: 720px) { .gp-types { grid-template-columns:1fr; } }
          .gp-type { position:relative; display:flex; align-items:center; gap:14px; padding:16px 18px; border-radius:16px; border:1.5px solid #e5e7eb; background:white; cursor:pointer; text-align:left; transition:all .18s; box-shadow:0 2px 8px rgba(15,23,42,.04); }
          .gp-type:hover { border-color:#cbd5e1; }
          .gp-type.active { box-shadow:0 8px 24px rgba(15,23,42,.08); }
          .gp-type-icon { width:46px; height:46px; border-radius:12px; display:flex; align-items:center; justify-content:center; flex-shrink:0; font-size:19px; }
          .gp-type-count { margin-left:auto; font-size:26px; font-weight:800; line-height:1; }
          .gp-type-sub { margin-top:6px; display:inline-flex; align-items:center; gap:5px; font-size:11.5px; font-weight:700; padding:3px 8px; border-radius:20px; }

          .gp-toolbar { background:white; border-radius:16px; padding:14px 18px; margin-bottom:16px; box-shadow:0 2px 8px rgba(30,64,175,.08); border:1px solid rgba(59,130,246,.12); border-top:3px solid var(--gp-accent); }
          .gp-seg { display:inline-flex; gap:4px; background:#f1f5f9; border-radius:12px; padding:4px; }
          .gp-seg-btn { border:none; background:transparent; padding:7px 18px; border-radius:9px; font-size:13px; font-weight:600; color:#64748b; cursor:pointer; transition:all .15s; }
          .gp-seg-btn:hover { color:#1e40af; }
          .gp-seg-btn.active { background:white; color:#1e40af; box-shadow:0 1px 4px rgba(15,23,42,.1); }
          .status-chip { display:inline-flex; align-items:center; gap:6px; padding:6px 13px; border-radius:20px; font-size:12px; font-weight:600; cursor:pointer; border:1px solid #e5e7eb; background:white; color:#4b5563; transition:all .15s; }
          .status-chip:hover { border-color:#93c5fd; }

          .gp-return { background:white; border-radius:16px; padding:14px 18px; margin-bottom:16px; border:1px solid rgba(20,184,166,.3); border-left:4px solid #0f766e; box-shadow:0 2px 8px rgba(15,118,110,.08); }
          .gp-return-icon { width:40px; height:40px; border-radius:11px; display:flex; align-items:center; justify-content:center; background:linear-gradient(135deg,#0f766e,#14b8a6); color:white; font-size:16px; flex-shrink:0; }
          .gp-return-done { margin-top:10px; display:inline-flex; align-items:center; gap:7px; padding:6px 12px; border-radius:10px; background:rgba(22,163,74,.1); color:#15803d; font-size:12.5px; font-weight:700; }
        `}</style>

        <GlobalHeaderTitle
          title="Gate Passes"
          subtitle={
            dateScope === 'today'
              ? 'Students leaving school during the day'
              : 'All early departures and short leaves'
          }
          icon="pi pi-ticket"
          buttons={[
            {
              label: 'Refresh',
              icon: 'pi pi-refresh',
              onClick: refreshAll,
              outlined: true,
              disabled: loading,
              style: { borderWidth: '2px' },
            },
            {
              label: `New ${typeMeta.label.toLowerCase()}`,
              icon: 'pi pi-plus',
              onClick: () => openNewLeave(),
            },
          ]}
        />

        {/* ── Pass type switch — the main split of this screen ── */}
        <div className="gp-types">
          {PASS_TYPES.map((t) => {
            const meta = PASS_TYPE_META[t];
            const active = passType === t;
            const count = counts ? counts[t] : null;
            return (
              <button
                key={t}
                type="button"
                className={`gp-type ${active ? 'active' : ''}`}
                onClick={() => handleTypeChange(t)}
                style={active ? { borderColor: meta.color, background: meta.bg } : undefined}
              >
                <div
                  className="gp-type-icon"
                  style={
                    active
                      ? { background: meta.gradient, color: 'white' }
                      : { background: meta.bg, color: meta.color }
                  }
                >
                  <i className={meta.icon} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15.5, fontWeight: 700, color: active ? meta.color : '#1e293b' }}>
                    {meta.plural}
                  </div>
                  <div style={{ fontSize: 12.5, color: '#64748b', fontWeight: 500, marginTop: 2 }}>{meta.hint}</div>
                  {t === 'short_leave' && counts && counts.outNow > 0 && (
                    <div
                      className="gp-type-sub"
                      style={{ color: STATUS_META.out.color, background: STATUS_META.out.bg }}
                    >
                      <i className="pi pi-sign-out" style={{ fontSize: 10 }} />
                      {counts.outNow} outside right now
                    </div>
                  )}
                </div>
                {count !== null && (
                  <div className="gp-type-count" style={{ color: active ? meta.color : '#cbd5e1' }}>
                    {count}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* key: a fresh box (and last message) per tab */}
        <ScanPanel key={passType} passType={passType} onResult={handleScanResult} toast={toast} />

        {/* ── Toolbar (scoped to the selected type) ── */}
        <div className="gp-toolbar" style={{ ['--gp-accent' as any]: typeMeta.color }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="gp-seg" style={{ marginBottom: 2 }}>
              <button
                className={`gp-seg-btn ${dateScope === 'today' ? 'active' : ''}`}
                onClick={() => handleScopeChange('today')}
              >
                Today
              </button>
              <button
                className={`gp-seg-btn ${dateScope === 'all' ? 'active' : ''}`}
                onClick={() => handleScopeChange('all')}
              >
                All dates
              </button>
            </div>

            {dateScope === 'all' && (
              <div style={{ minWidth: 200, maxWidth: 240, flex: '0 1 220px' }}>
                <GlobalDateField
                  label="Date"
                  value={date}
                  onChange={(val) => {
                    setDate((val as Date | null) ?? null);
                    resetToFirstPage();
                  }}
                  placeholder="Any date"
                  dateFormat="dd M yy"
                  icon="pi pi-calendar"
                  showButtonBar
                  fullWidth
                />
              </div>
            )}

            <div style={{ marginLeft: 'auto', marginBottom: 8, fontSize: 13, fontWeight: 600, color: '#64748b' }}>
              <span style={{ color: typeMeta.color, fontWeight: 800 }}>{pagination.total_items}</span>{' '}
              {pagination.total_items === 1 ? typeMeta.label.toLowerCase() : typeMeta.plural.toLowerCase()}{' '}
              {scopeLabel}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
            {statusOptions.map((s) => {
              const active = status === s;
              const color = s === 'all' ? typeMeta.color : STATUS_META[s].color;
              return (
                <button
                  key={s}
                  className="status-chip"
                  onClick={() => {
                    setStatus(s);
                    resetToFirstPage();
                  }}
                  style={active ? { background: color, borderColor: color, color: 'white' } : undefined}
                >
                  {s !== 'all' && <i className={STATUS_META[s].icon} style={{ fontSize: 11 }} />}
                  {s === 'all' ? 'All' : statusLabel(s, passType).replace(' · ready to leave', '')}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Table / Skeleton ── */}
        {loading ? (
          <VisitorLogSkeletonLoader />
        ) : (
          <div
            style={{
              background: 'white',
              borderRadius: 20,
              boxShadow: '0 2px 12px rgba(0,0,0,.06)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              width: '100%',
              ['--gp-accent' as any]: typeMeta.color,
            }}
          >
            {passes.length > 0 && (
              <div
                style={{
                  padding: '14px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  background: typeMeta.bg,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 10,
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: typeMeta.color }}>
                  <i className={typeMeta.icon} style={{ fontSize: 13 }} />
                  {typeMeta.plural}
                  <span style={{ color: '#64748b', fontWeight: 600 }}>
                    · showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, pagination.total_items)} of{' '}
                    {pagination.total_items}
                  </span>
                </span>
                <div
                  style={{
                    padding: '6px 12px',
                    borderRadius: 8,
                    background: 'white',
                    border: `1px solid ${typeMeta.border}`,
                    fontSize: 13,
                    color: typeMeta.color,
                    fontWeight: 600,
                  }}
                >
                  Page {pagination.current_page} of {pagination.total_pages}
                </div>
              </div>
            )}

            <DataTable
              value={passes}
              loading={false}
              rowClassName={(row: GatePassRecord) =>
                [isOverdue(row) ? 'gp-row-overdue' : '', row.id === flashId ? 'gp-row-flash' : ''].join(' ')
              }
              emptyMessage={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 20px',
                    minHeight: 360,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      background: typeMeta.gradient,
                      borderRadius: 14,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 20,
                      boxShadow: `0 6px 18px ${typeMeta.border}`,
                    }}
                  >
                    <i className={emptyState.icon} style={{ fontSize: 25, color: 'white' }} />
                  </div>
                  <h3 style={{ margin: '0 0 8px', color: typeMeta.color, fontSize: 20, fontWeight: 700, textAlign: 'center' }}>
                    {emptyState.title}
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: '#64748b',
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: 'center',
                      maxWidth: 400,
                      lineHeight: 1.6,
                    }}
                  >
                    {emptyState.message}
                  </p>
                </div>
              }
            >
              <Column header="#" body={serialTemplate} style={{ width: 52 }} />
              <Column header="Student" body={studentTemplate} />
              <Column header="Reason" body={reasonTemplate} />
              <Column header={isShortLeave ? 'Goes out with' : 'Picked up by'} body={pickupTemplate} />
              {dateScope === 'all' && <Column header="Date" body={dateTemplate} style={{ width: 120 }} />}
              {!isShortLeave && <Column header="Requested" body={requestedTemplate} style={{ width: 100 }} />}
              {isShortLeave && <Column header="Went out" body={wentOutTemplate} style={{ width: 100 }} />}
              {isShortLeave && <Column header="Back by" body={backByTemplate} style={{ width: 100 }} />}
              {isShortLeave && <Column header="Back in" body={backInTemplate} style={{ width: 110 }} />}
              {!isShortLeave && <Column header="Approval" body={approvalTemplate} style={{ width: 110 }} />}
              <Column header="Status" body={statusTemplate} style={{ width: 190 }} />
              <Column header="Actions" body={actionTemplate} style={{ width: 130 }} />
            </DataTable>

            {pagination.total_pages > 1 && (
              <CustomPaginator
                page={pagination.current_page}
                totalPages={pagination.total_pages}
                totalRecords={pagination.total_items}
                pageSize={pageSize}
                onPageChange={(newPage) => setPage(newPage)}
                onPageSizeChange={(newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                }}
                showInfoBar={false}
              />
            )}
          </div>
        )}
        <style>{`
          .gp-row-overdue > td { background: rgba(254,242,242,.7) !important; }
          @keyframes gpFlash { 0%,100% { background: transparent; } 50% { background: rgba(20,184,166,.28); } }
          .gp-row-flash > td { animation: gpFlash .5s ease-in-out 2; }
        `}</style>
      </div>

      <ViewGatePassModal
        open={isViewOpen}
        passId={viewTarget?.id ?? null}
        studentName={viewTarget?.studentName ?? ''}
        spStudentId={viewTarget?.spStudentId ?? ''}
        onClose={() => setIsViewOpen(false)}
        onChanged={refreshAll}
      />

      <GatePassFormModal
        open={isFormOpen}
        initialPassType={passType}
        prefillStudent={prefillStudent}
        onClose={() => setIsFormOpen(false)}
        onSuccess={handleFormSuccess}
      />
    </>
  );
};

const serialStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  background: '#f9fafb',
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 12,
  color: '#6b7280',
  margin: '0 auto',
};

const avatarStyle: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 10,
  flexShrink: 0,
  color: 'white',
  fontSize: 13,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const nameStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 13,
  color: '#1f2937',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const ellipsisStyle: React.CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export default GatePassDashboard;